import { useEffect, useMemo } from 'react';

import getOr from 'lodash/fp/getOr';
import map from 'lodash/fp/map';
import stubFalse from 'lodash/fp/stubFalse';
import upperCase from 'lodash/fp/upperCase';

import { getCurrentContentAsCommunity } from '@lumapps/communities/ducks/selectors';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import { useLocation } from '@lumapps/router';
import { selectSpaceData } from '@lumapps/spaces/ducks/selectors';
import { WIDGET_COMMUNITY_LIST_TYPE } from '@lumapps/widget-community-list/constants';
import { getReadGenericListURLFilters } from '@lumapps/widget-content-filter/utils/getReadGenericListURLFilters';
import { WIDGET_CONTENT_LIST_TYPE } from '@lumapps/widget-content-list/constants';
import { WIDGET_DIRECTORY_ENTRIES_TYPE } from '@lumapps/widget-directory-entries/constants';
import { WIDGET_COMMUNITY_POST_LIST_TYPE } from '@lumapps/widget-post-list/constants';
import {
    UrlPostListFiltersOptionType,
    getPostListURLFilters,
} from '@lumapps/widget-post-list/utils/PostListFilters/getPostListURLFilters';

import { actions } from '../ducks/slice';
import { Widget } from '../types';

export interface UseWidgetURLFiltersProps {
    isMainWidget?: Widget['isMainWidget'];
    widgetId?: Widget['widgetId'];
    widgetType?: Widget['widgetType'];
}

export interface GetWidgetURLFiltersByTypeOptions {
    isLegacyCompatible?: boolean;
}

const GetWidgetURLFiltersByType: Record<
    string,
    (search: string, options?: GetWidgetURLFiltersByTypeOptions) => Record<string, any>
> = {
    [WIDGET_COMMUNITY_POST_LIST_TYPE]: getPostListURLFilters,
    [WIDGET_COMMUNITY_LIST_TYPE]: getReadGenericListURLFilters,
    [WIDGET_CONTENT_LIST_TYPE]: getReadGenericListURLFilters,
    [WIDGET_DIRECTORY_ENTRIES_TYPE]: getReadGenericListURLFilters,
};

export const useWidgetURLFilters = ({ isMainWidget, widgetId, widgetType }: UseWidgetURLFiltersProps) => {
    const dispatch = useDispatch();
    const { search } = useLocation();
    const currentSpace = useSelector(selectSpaceData);
    const currentCommunity = useSelector(getCurrentContentAsCommunity);
    const availablePostTypes = useMemo(
        () => map(upperCase, getOr([], 'postTypes', currentSpace || currentCommunity)),
        [currentCommunity, currentSpace],
    );

    const filters = useMemo(() => {
        const getUrlFilters =
            widgetType && isMainWidget && GetWidgetURLFiltersByType[widgetType]
                ? GetWidgetURLFiltersByType[widgetType]
                : stubFalse;

        return widgetType === WIDGET_COMMUNITY_POST_LIST_TYPE
            ? getUrlFilters(search, {
                  isLegacyCompatible: true,
                  availablePostTypes,
              } as UrlPostListFiltersOptionType)
            : getUrlFilters(search, { isLegacyCompatible: true });
    }, [availablePostTypes, isMainWidget, search, widgetType]);

    useEffect(() => {
        if (filters && widgetId) {
            dispatch(
                actions.setWidgetFilters({
                    widgetId,
                    filters,
                }),
            );
        }
        // this use Effect should run only the first time to initialize the filters from URL
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return filters;
};
